/* eslint-disable react/jsx-props-no-spreading */
import { useResizeObserver } from '@folklore/hooks';
import classNames from 'classnames';
import camelCase from 'lodash/camelCase';
import { useCallback, useRef, useState } from 'react';

import useGameState from '../../hooks/useGameState';

import { useThemeClassName } from '../../contexts/ThemeContext';
import GamePoints from '../partials/GamePoints';
import BaseGame, { GameApi } from './Base';
import DownfallPixi from './Downfall/DownfallPixi';

import styles from '../../../styles/games/downfall.module.css';

interface DownfallGameProps {
    game: DownfallGame;
    participation: Participation;
    canPlay: boolean;
    withQuestionsIndicator?: boolean;
    apiRef?: React.MutableRefObject<GameApi>;
    reload: () => void;
    children?: React.ReactNode;
    className?: string;
    // questionClassName?: string;
    // questionContainerClassName?: string;
    navigationClassName?: string;
    onLoadingChange?: (loading: boolean) => void;
}

function DownfallGame({
    game,
    participation,
    canPlay,
    reload,
    apiRef: providedApiRef = null,
    className,
    // questionClassName,
    // questionContainerClassName,
    onLoadingChange: providedOnLoadingChange = null,
    ...props
}: DownfallGameProps) {
    const [loading, setLoading] = useState<boolean>(false);
    const themeClassName = useThemeClassName(styles);
    const {
        ref: containerRef,
        entry: { contentRect },
    } = useResizeObserver();

    const { type: gameType } = game || {};

    const {
        // answers: userAnswers = [],
        ended = false,
        points: initialPoints = 0,
    } = participation || {};

    const { currentQuestionIndex, currentQuestion, allAnswersValid } = useGameState(
        game,
        participation,
    );
    const { id: questionId, type: questionType, winningScore = null } = currentQuestion || {};

    const apiRef = useRef<GameApi>(null);
    const { muted = false } = apiRef.current || {};

    const [points, setPoints] = useState<number>(initialPoints);
    const onHit = useCallback(
        (score) => {
            setPoints(score);
        },
        [setPoints],
    );

    const onAnswer = useCallback(
        (newPoints) => {
            const { answerQuestion } = apiRef.current;
            answerQuestion(questionId, newPoints);
        },
        [questionId],
    );

    const onLoadingChange = useCallback(
        (newLoading: boolean) => {
            setLoading(newLoading);
            if (providedOnLoadingChange !== null) {
                providedOnLoadingChange(newLoading);
            }
        },
        [providedOnLoadingChange],
    );

    return (
        <BaseGame
            game={game}
            participation={participation}
            apiRef={(ref) => {
                apiRef.current = ref;
                if (providedApiRef !== null) {
                    providedApiRef.current = ref;
                }
            }}
            reload={reload}
            canPlay={canPlay}
            withFeedback
            withTimer={'duration' in game}
            isValid={allAnswersValid}
            className={classNames([
                styles.container,
                styles[camelCase(`${gameType}-game`)],
                styles[camelCase(`${questionType}-question`)],
                className,
                themeClassName,
            ])}
            onLoadingChange={onLoadingChange}
            statusBar={
                points !== null ? (
                    <GamePoints
                        className={styles.points}
                        points={points}
                        maximum={winningScore}
                        withMaximum
                    />
                ) : null
            }
            {...props}
        >
            <div
                className={classNames([styles.gameContainer, 'game-container-downfall'])}
                ref={containerRef}
            >
                {!ended && !loading ? (
                    <DownfallPixi
                        key={`question-${currentQuestionIndex}`}
                        game={game}
                        options={currentQuestion}
                        initialPoints={initialPoints}
                        onHit={onHit}
                        onEnd={onAnswer}
                        className={styles.game}
                        size={contentRect}
                        muted={muted}
                    />
                ) : null}
            </div>
        </BaseGame>
    );
}

export default DownfallGame;
